import { Component, input } from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
    TableRowData,
    hasPalletTickets,
    hasDiscrepancies,
    hasFineDiscrepancies,
} from '@app/shared/models/table-data-types';
import {
    ColumnConfig,
    ColumnValue,
} from '@app/shared/services/view-configs/column-creator';

@Component({
    selector: 'app-status-badge',
    template: `
        <div class="align-items-center tw-flex tw-flex-row tw-align-middle">
            <span
                [class]="getBadgeClass()"
                class="tw-px-2 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium">
                {{ getTranslatedStatusText() | translate }}
            </span>

            <!-- Pallet tickets indicator -->
            @if (hasPalletTickets) {
                <img
                    class="tw-ml-1.5"
                    [matTooltip]="
                        'OUTGOING-GOODS.FOLDER-VIEW.HAS-PALLETTICKET'
                            | translate
                    "
                    src="assets/images/load_carrier_icon.svg"
                    alt="exists"
                    width="23"
                    height="23" />
            }
            @if (showDiscrepanciesIcon() && hasAnyDiscrepancies) {
                <mat-icon
                    class="tw-ml-1.5"
                    [matTooltip]="
                        'OUTGOING-GOODS.FOLDER-VIEW.HAS-DISCREPANCIES'
                            | translate
                    "
                    svgIcon="red-error">
                </mat-icon>
            }
        </div>
    `,
    standalone: true,
    imports: [TranslateModule, MatIconModule, MatTooltipModule],
})
export class StatusBadgeComponent {
    value = input<ColumnValue>();
    row = input<TableRowData>();
    column = input<ColumnConfig>();
    index = input<number>(0);
    parentRow = input<TableRowData>();
    showDiscrepanciesIcon = input<boolean>(false);

    getBadgeClass(): string {
        const baseClasses =
            'tw-px-2 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium';

        // TODO: Colors temporarily disabled - reimplement color scheme
        /*
        switch (this.value?.toLowerCase()) {
            case 'open':
                return `${baseClasses} tw-bg-blue-100 tw-text-blue-800`;
            case 'sent':
                return `${baseClasses} tw-bg-green-100 tw-text-green-800`;
            case 'closed':
                return `${baseClasses} tw-bg-gray-100 tw-text-gray-800`;
            case 'completed':
                return `${baseClasses} tw-bg-green-100 tw-text-green-800`;
            case 'pending':
                return `${baseClasses} tw-bg-yellow-100 tw-text-yellow-800`;
            case 'cancelled':
                return `${baseClasses} tw-bg-red-100 tw-text-red-800`;
            default:
                return `${baseClasses} tw-bg-gray-100 tw-text-gray-800`;
        }
        */
        return baseClasses;
    }

    getTranslatedStatusText(): string {
        if (!this.value()) return '';
        // Convert value to string before calling toUpperCase
        const statusText = String(this.value()).toUpperCase();
        return `OUTGOING-GOODS.DELIVERY-NOTE-BUNDLE.STATUS.${statusText}`;
    }

    get hasPalletTickets(): boolean {
        const row = this.row() as TableRowData;
        return hasPalletTickets(row) && row.palletTickets;
    }

    get hasAnyDiscrepancies(): boolean {
        const row = this.row();
        if (!row) return false;

        if (hasDiscrepancies(row)) {
            const data = row as TableRowData & {
                discrepancies?: boolean;
                hasDiscrepancies?: boolean;
            };
            if (data.discrepancies === true || data.hasDiscrepancies === true)
                return true;
        }

        if (hasFineDiscrepancies(row)) {
            const data = row as TableRowData & {
                fineDiscrepancies?: boolean;
                hasFineDiscrepancies?: boolean;
            };
            if (
                data.fineDiscrepancies === true ||
                data.hasFineDiscrepancies === true
            )
                return true;
        }

        return false;
    }
}
